/* eslint-disable no-restricted-globals */
/* eslint-disable prefer-template */
/* eslint-disable fp/no-mutating-methods */
/* eslint-disable fp/no-mutation */
import { ReactElement, useState, useEffect } from 'react'
import { uniq } from 'ramda'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  CloseIcon,
  ExpandIcon,
  UserIcon,
  ViewStatsIcon,
} from 'src/v3/core/icons'
import { UserExtra as User, UserExtra } from 'src/user/user.types'
import theme from 'src/theme'
import { UserGroup } from '../manage.slice'

export type UserCourseData = {
  pending: string[]
  completed: string[]
}

interface Props {
  users: User[]
  groups?: UserGroup[]
  stats?: boolean
  checkbox?: {
    onClick: (id: string[]) => void
    checked: string[]
  }
  userCourseData?: Record<string, UserCourseData>
  onApproveUser?: (status: string) => void
}

interface CourseListProps {
  user: UserExtra
  courseData?: UserCourseData
  onCancel: () => void
}

let CourseList = ({
  courses,
  label,
  dates,
}: {
  courses: string[]
  label: 'Completed' | 'Pending'
  dates?: Record<string, string>
}): ReactElement => (
  <div>
    <Divider />
    <div style={{ padding: 12 }}>
      <div className="pb-1 font-weight-bold">{label}</div>
      <div
        style={{
          maxHeight: '25vh',
          overflowWrap: 'break-word',
          overflowY: 'scroll',
        }}
      >
        <ul style={{ marginBottom: 0 }}>
          {(courses.length ? courses : ['None']).map((c, i) => (
            <li key={`${c}-${i}`}>
              {'\u2022 ' +
                (label === 'Completed' && dates && c !== 'None'
                  ? `${dates[c]}: ${c}`
                  : c)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
)

let UserCard = ({
  user,
  courseData,
  onCancel,
}: CourseListProps): ReactElement => {
  let completionRate = courseData
    ? Math.round(
        (100 * courseData.completed.length) /
          (courseData.completed.length + courseData.pending.length),
      )
    : NaN

  return (
    <Card variant="outlined" className="p-3">
      <Box className="flex items-center px-2 mb-2 justify-content-between">
        <div className="flex items-center flex-grow my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <UserIcon />
          </Avatar>
          <Typography className="pl-2">{user.name}</Typography>
        </div>
        <IconButton
          edge="end"
          aria-label="close"
          onClick={onCancel}
          sx={{ marginTop: -3, marginLeft: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {!courseData ? (
        <Box
          sx={{
            padding: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          idk
        </Box>
      ) : (
        <Box
          sx={{
            padding: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <div style={{ padding: 12, overflowWrap: 'break-word' }}>
            <div className="font-weight-bold">Completion Rate</div>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
              <Box sx={{ width: 'fit-content' }}>
                <Typography variant="body2" color="text.secondary">
                  {completionRate}%
                </Typography>
              </Box>
              <Box sx={{ width: '100%', minWidth: 180 }}>
                <LinearProgress variant="determinate" value={completionRate} />
              </Box>
            </Box>
          </div>
          <CourseList courses={courseData.pending} label="Pending" />
          <CourseList
            courses={courseData.completed}
            label="Completed"
            // dates={courseData.completedDates}
          />
        </Box>
      )}
    </Card>
  )
}

export function UserList({
  users,
  groups,
  stats,
  checkbox,
  userCourseData,
}: Props): ReactElement {
  let [page, setPage] = useState<number>(1)
  let [nameFilter, setNameFilter] = useState<string>('')
  let [groupFilter, setGroupFilter] = useState<string>('')
  let [dialogStatus, setDialogStatus] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(nameFilter.toLowerCase()) &&
      (!groups ||
        (groups.find((g) => g.id === groupFilter)?.users?.indexOf(user.id!) ??
          1) > -1),
  )
  useEffect(() => {
    if (filteredUsers.length <= (page - 1) * 10) {
      setPage(page - 1 || 1)
    }
  }, [filteredUsers])

  let handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  let handleCheckboxToggle = (value: string) => {
    let { checked } = checkbox!
    let currentIndex = checked.indexOf(value)
    let newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    return newChecked
  }

  let selectAll = (checked: string[], filtered: User[]): string[] => {
    let checkedFiltered = checked.filter((c) =>
      filtered.some((f) => f.id === c),
    )

    if (filtered.length === checkedFiltered.length) {
      return checked.filter((c) => !filtered.some((f) => f.id === c))
    }

    return uniq([...checked, ...filtered.map((f) => f.id)])
  }

  let [errTitle, errMsg] = errorMessage.split(':')

  let overlayStyle = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <Paper variant="outlined" elevation={0} className="mt-2 mb-4">
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandIcon />}>Filters</AccordionSummary>
        <AccordionDetails
          className="flex px-3 pt-0 justify-content-between"
          sx={{
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <>
            <TextField
              variant="outlined"
              label="Name"
              name="name"
              type="text"
              size="small"
              value={nameFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNameFilter(event.target.value)
              }}
              sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
            />
            {groups && (
              <FormControl sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}>
                <InputLabel size="small" sx={{ paddingRight: 2 }}>
                  Group
                </InputLabel>
                <Select
                  value={groupFilter}
                  label="Groups"
                  variant="outlined"
                  size="small"
                  onChange={(event) => setGroupFilter(event.target.value)}
                >
                  <MenuItem key={'none'} value={''}>
                    None
                  </MenuItem>
                  {groups.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        </AccordionDetails>
      </Accordion>
      {checkbox && (
        <ListItem dense divider>
          <ListItemIcon>
            <Tooltip title="Select All" placement="top">
              <Checkbox
                edge="start"
                checked={filteredUsers.every(
                  (item) => checkbox.checked.indexOf(item.id) >= 0,
                )}
                tabIndex={-1}
                disableRipple
                onClick={() =>
                  checkbox.onClick(selectAll(checkbox.checked, filteredUsers))
                }
              />
            </Tooltip>
          </ListItemIcon>
          <ListItemText id={'header'} primary="Name" />
        </ListItem>
      )}
      <List
        sx={{
          overflow: 'auto',
          width: '100%',
          backgroundColor: 'background.paper',
          minHeight: 247,
          height: users.length >= 10 ? 620 : users.length * 62,
        }}
        disablePadding
      >
        {filteredUsers.slice(10 * (page - 1), 10 * page).map((user) => {
          let totalCourses = userCourseData?.[user.id]
            ? userCourseData[user.id].completed.length +
              userCourseData[user.id].pending.length
            : 0
          let percentage = userCourseData?.[user.id]
            ? Math.round(
                (100 * userCourseData[user.id].completed.length) /
                  (userCourseData[user.id].completed.length +
                    userCourseData[user.id].pending.length),
              )
            : NaN

          return (
            <ListItem
              key={user.id}
              secondaryAction={
                checkbox ? undefined : (
                  <>
                    {stats && (
                      <>
                        {/* <Tooltip title='Give Credit'>
                    <IconButton
                      onClick={() => onApproveUser?.(user.id)}
                    >
                      <GiveCreditIcon />
                    </IconButton>
                  </Tooltip> */}
                        <Tooltip title="Status">
                          <IconButton
                            onClick={() => setDialogStatus(`info-${user.id}`)}
                          >
                            <ViewStatsIcon />
                          </IconButton>
                        </Tooltip>
                        <Dialog
                          open={dialogStatus === `info-${user.id}`}
                          onClose={() => setDialogStatus('')}
                          scroll="paper"
                          fullWidth={true}
                          maxWidth="xs"
                        >
                          <UserCard
                            user={user}
                            courseData={userCourseData?.[user.id]}
                            onCancel={() => setDialogStatus('')}
                          />
                        </Dialog>
                      </>
                    )}
                  </>
                )
              }
              sx={{ pr: checkbox ? 0 : 12 }}
              disablePadding
              divider
            >
              {!checkbox ? (
                <>
                  <Tooltip
                    title={`${userCourseData?.[user.id]?.completed.length || 0} / ${
                      totalCourses
                    } Courses Complete`}
                    placement="left"
                  >
                    <span style={{ marginLeft: 4, marginRight: 4 }}>
                      <IconButton disabled>
                        <Box sx={{ position: 'relative', display: 'flex' }}>
                          <CircularProgress
                            color="inherit"
                            variant="determinate"
                            value={100}
                            size={36}
                          />
                          <Box sx={overlayStyle}>
                            <CircularProgress
                              color={percentage === 100 ? 'success' : 'primary'}
                              variant="determinate"
                              value={isNaN(percentage) ? 0 : percentage}
                              size={36}
                            />
                          </Box>
                          <Box sx={overlayStyle}>
                            <Typography
                              color="text.primary"
                              sx={{ fontSize: 11 }}
                            >
                              {isNaN(percentage)
                                ? 'N/A'
                                : `${Math.round(percentage)}%`}
                            </Typography>
                          </Box>
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <ListItemText
                    primary={user.name}
                    secondary={user.primaryEmail}
                    secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                    sx={{ py: 0.5 }}
                  />
                </>
              ) : (
                <ListItemButton
                  onClick={() =>
                    checkbox.onClick(handleCheckboxToggle(user.id))
                  }
                  sx={{ py: '2px' }} // remove if 49 and not 62
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checkbox?.checked.indexOf(user.id!) !== -1}
                      tabIndex={-1}
                      disableRipple
                      sx={{ py: 1 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name}
                    secondary={user.primaryEmail}
                    secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                    sx={{ py: 0.5 }}
                  />
                </ListItemButton>
              )}
            </ListItem>
          )
        })}
      </List>
      <Divider />
      <Pagination
        size="small"
        sx={{ paddingY: '5px', display: 'flex', justifyContent: 'center' }}
        count={Math.ceil(filteredUsers.length / 10)}
        page={page}
        onChange={handlePageChange}
        showFirstButton
        showLastButton
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>{errTitle}:</strong>
          {errMsg}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default UserList
